import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { getEducationText, suggestEducation } from "../lib/patientEducation";
import { patientLanguageName } from "../lib/patientLanguages";
import { CLINIC } from "../lib/clinic";
import { openWhatsAppRaw } from "../lib/whatsapp";

export const PatientEducationHandouts = ({ patient, known, medicines, allergyFlags, selected, onChange }) => {
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const lang = patient.preferred_language || "en";
  const suggestions = suggestEducation({ known, medicines, allergyFlags });
  const share = async (item) => {
    setBusy(item.id);
    try {
      // Re-check preference on the server in case another window changed it.
      await api.post("/education/share", { patient_id: patient.patient_id, topic: item.title, module_id: item.id, language: lang });
      openWhatsAppRaw(patient.mobile, `*${item.title}*\n\n${item.body}\n\n— ${[CLINIC.doctorName, CLINIC.name].filter(Boolean).join(", ")}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open education sharing"); }
    finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid="education-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-600" data-testid="education-selected-language">{patientLanguageName(lang)}</span>
        <button className="text-xs text-pink-700 flex items-center gap-1 hover:underline" data-testid="education-language-hub"
          onClick={() => nav(`/education?pid=${patient.patient_id}`)}><BookOpen size={13} /> Education Hub</button>
      </div>
      {!suggestions.length && <p className="text-xs text-slate-500" data-testid="education-no-suggestions">No handouts suggested for the current diagnosis or medicines.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {suggestions.map((e) => {
          const item = getEducationText(e.id, lang);
          const checked = selected.includes(e.id);
          return (
            <div key={e.id} data-testid={`education-item-${e.id}`} className={`border rounded-md p-2 text-xs ${checked ? "border-pink-400 bg-pink-50/60" : "border-slate-200 bg-white"}`}>
              <label className="flex items-start gap-2">
                <input type="checkbox" className="mt-1 accent-pink-600" checked={checked} disabled={!item && !checked}
                  data-testid={`education-toggle-${e.id}`} onChange={() => onChange(checked ? selected.filter((x) => x !== e.id) : [...selected, e.id])} />
                <div className="min-w-0 flex-1">
                  <div data-testid={`education-title-${e.id}`} className="font-semibold">{item?.title || e.title}</div>
                  {item ? <p data-testid={`education-body-${e.id}`} lang={lang} className="text-slate-600 mt-1 line-clamp-3">{item.body}</p>
                    : <p role="status" data-testid={`education-unavailable-${e.id}`} className="text-amber-800 mt-1">Handout unavailable in {patientLanguageName(lang)}. Open Education Hub for a language-specific article.</p>}
                </div>
              </label>
              {patient.mobile && <button disabled={!item || !!busy} onClick={() => share(item)}
                data-testid={`education-whatsapp-${e.id}`} className="mt-2 text-emerald-800 flex items-center gap-1 disabled:opacity-40 hover:underline">
                <MessageCircle size={12} /> {busy === e.id ? "Opening…" : `WhatsApp · ${patientLanguageName(lang)}`}
              </button>}
            </div>
          );
        })}
      </div>
      {!!selected.length && <p data-testid="education-selected-count" className="text-xs text-pink-700">{selected.length} selected · only available translations will print.</p>}
    </div>
  );
};